import { useState } from 'react';
import { ExternalLink, Loader2, Utensils } from 'lucide-react';

import { CollapsibleCard } from '@/components/admin/CollapsibleCard';
import {
  useFoodserviceEntitlement,
  useSetFoodserviceEntitlement,
} from '@/hooks/admin/useFoodservice';

/**
 * Foodservice equipment management — the entitlement, and the button that
 * provisions the tenant behind it.
 *
 * THE TWO LANES (Sky, 2026-08-21), which this card exists to keep straight:
 *
 *   Chain customer (The Melt, Starbird, their stores)
 *     → equipment requisition, purchasing, inventory, shipping and install run
 *       in the FOODSERVICE PORTAL. ERLS is called only for the lease.
 *       This box gets ticked.
 *
 *   Independent customer
 *     → orders beverage here; buys equipment in ERLS through its own purchase
 *       + requisition → PO → QuickBooks path. No foodservice portal, ever.
 *       This box stays clear.
 *
 * The card therefore leads with which lane the account is in, not with the
 * checkbox — the checkbox is only obvious once you know that. It WARNS rather
 * than blocks on a non-chain account: chain membership is derived from live
 * QuickBooks parent links, and an operator standing a brand up may legitimately
 * be ahead of QuickBooks.
 */
export default function FoodservicePortalCard({ customerId }: { customerId: string }) {
  const { data, isLoading } = useFoodserviceEntitlement(customerId);
  const setFlag = useSetFoodserviceEntitlement(customerId);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const enabled = data?.enabled === true;
  const chainRole = data?.chain.role ?? 'none';
  const isChain = chainRole !== 'none';
  // The portal's own answer wins over our flag — they can drift if someone
  // created the tenant by hand over there, or a grant died after it landed.
  const tenant = data?.portal_tenant ?? null;

  const summary = isLoading
    ? '…'
    : enabled
      ? tenant
        ? `On · tenant "${tenant.name}"`
        : 'On · tenant not found in the portal'
      : tenant
        ? 'Off here · but a tenant exists in the portal'
        : 'Off — equipment goes through ERLS';

  const toggle = async (value: boolean) => {
    setErr(null);
    setOk(null);
    try {
      const res = await setFlag.mutateAsync(value);
      if (!value) {
        setOk(res.message ?? 'Entitlement removed.');
      } else if (res.created) {
        const made = res.stores?.created ?? 0;
        setOk(
          `Tenant created${made ? ` with ${made} store${made === 1 ? '' : 's'} imported` : ''}. Invite the people who need portal access from the portal's Setup → Customers → Users.`,
        );
      } else {
        setOk('Already provisioned — the entitlement is now recorded here too.');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <CollapsibleCard
      title="Foodservice equipment portal"
      icon={<Utensils size={14} className="text-brix-muted shrink-0" />}
      storageKey={`foodservice-${customerId}`}
      defaultOpen={enabled}
      tone={enabled ? 'active' : undefined}
      summary={summary}
      desc={
        isChain
          ? 'Chain accounts run equipment requisition, purchasing, inventory, shipping and install in the foodservice portal. ERLS is called only when a line is leased.'
          : 'Independent accounts buy equipment through ERLS (purchase + requisition → PO → QuickBooks) and order beverage here. They do not need the foodservice portal.'
      }
    >
      <div className="space-y-3">
        {isLoading ? (
          <p className="text-xs text-brix-muted">Loading…</p>
        ) : !data ? (
          <p className="text-xs text-brix-warning">Could not read the entitlement.</p>
        ) : (
          <>
            {/* Which lane, stated plainly, before the control. */}
            <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2 text-[11px] text-brix-muted">
              {chainRole === 'master' ? (
                <>
                  This account is a{' '}
                  <span className="font-semibold text-brix-ink">
                    {data.chain.groupKind === 'franchise' ? 'franchise owner' : 'chain HQ'}
                  </span>
                  . Granting it provisions the brand tenant and imports its QuickBooks stores.
                </>
              ) : chainRole === 'member' ? (
                <>
                  Store under{' '}
                  <span className="font-semibold text-brix-ink">{data.chain.masterName}</span>. It
                  provisions under that brand in the portal.
                </>
              ) : (
                <>
                  No chain group on this account — it reads as an independent, so equipment belongs
                  in ERLS. Tick the box anyway only if you know it is a chain that QuickBooks
                  doesn&apos;t link yet.
                </>
              )}
            </div>

            {!data.configured && (
              <p className="text-xs text-brix-warning">
                <span className="font-semibold">PROVISION_SECRET is not set on this site</span> — the
                portal can&apos;t be called until it holds the same value the portal does.
              </p>
            )}

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                disabled={setFlag.isPending || !data.configured}
                onChange={(e) => {
                  const v = e.target.checked;
                  const msg = v
                    ? 'Grant the foodservice equipment portal? This creates (or re-uses) a tenant in the portal.'
                    : 'Remove the entitlement here? The tenant and its data stay in the portal — this only un-ticks the box.';
                  if (window.confirm(msg)) void toggle(v);
                }}
                className="mt-0.5 h-4 w-4 accent-emerald-600"
              />
              <span>
                <span className="text-sm font-semibold text-brix-ink">
                  Foodservice equipment management
                </span>
                <span className="block text-[11px] text-brix-muted mt-0.5">
                  {enabled
                    ? 'Equipment for this account is requisitioned and fulfilled in the foodservice portal.'
                    : 'Equipment for this account goes through ERLS.'}
                </span>
              </span>
            </label>

            {setFlag.isPending && (
              <p className="flex items-center gap-1.5 text-xs text-brix-muted">
                <Loader2 size={12} className="animate-spin" /> Talking to the portal…
              </p>
            )}

            {tenant && (
              <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 px-3 py-2 text-[11px] text-brix-muted space-y-1">
                <div>
                  Portal tenant{' '}
                  <span className="font-semibold text-brix-ink">{tenant.name}</span>
                  {tenant.customer_kind ? ` · ${tenant.customer_kind}` : ''}
                </div>
                <a
                  href="https://alamedapointbg.com/melt/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-brix-primary hover:underline"
                >
                  Open the foodservice portal <ExternalLink size={11} />
                </a>
                <div>
                  Provisioning does not create logins — invite people in the portal under Setup →
                  Customers → this customer → Users.
                </div>
              </div>
            )}

            {!enabled && tenant && (
              <p className="text-[11px] text-brix-warning">
                A tenant already exists in the portal for this QuickBooks customer while the box here
                is clear. Ticking the box records the entitlement without creating anything new.
              </p>
            )}

            {data.probe_error && (
              <p className="text-[11px] text-brix-muted">
                Couldn&apos;t read the portal just now ({data.probe_error}) — the box below still
                reflects what we recorded here.
              </p>
            )}

            {data.note && <p className="text-[11px] text-brix-muted">{data.note}</p>}
            {ok && <p className="text-xs text-emerald-600">{ok}</p>}
            {err && <p className="text-xs text-brix-warning">{err}</p>}
          </>
        )}
      </div>
    </CollapsibleCard>
  );
}
